<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Icon from './Icon.vue'

/** Full-screen viewer for the images/videos of one message. Esc / backdrop closes, ← → switch, click zooms. */
const props = defineProps<{ items: { type: string; url: string; name?: string | null }[]; start: number }>()
const emit = defineEmits<{ close: [] }>()

const idx = ref(Math.min(Math.max(props.start, 0), props.items.length - 1))
const zoom = ref(false)
const cur = computed(() => props.items[idx.value])
const many = computed(() => props.items.length > 1)
const closeBtn = ref<HTMLButtonElement | null>(null)
let returnFocus: HTMLElement | null = null

function go(d: number): void {
  if (!many.value) return
  idx.value = (idx.value + d + props.items.length) % props.items.length
}
watch(idx, () => (zoom.value = false))

function onKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') emit('close')
  else if (e.key === 'ArrowRight') go(1)
  else if (e.key === 'ArrowLeft') go(-1)
}
onMounted(() => {
  returnFocus = document.activeElement as HTMLElement | null
  document.addEventListener('keydown', onKey)
  void nextTick(() => closeBtn.value?.focus())
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKey)
  returnFocus?.focus?.()
})
</script>

<template>
  <Teleport to="body">
    <div v-if="cur" class="mv" role="dialog" aria-modal="true" aria-label="ดูรูปขนาดใหญ่" @click.self="emit('close')">
      <div class="mv-top">
        <span v-if="many" class="mv-n">{{ idx + 1 }} / {{ items.length }}</span>
        <a class="mv-btn" :href="cur.url" target="_blank" rel="noopener noreferrer" title="เปิดไฟล์เต็มในแท็บใหม่">
          <Icon name="external" /><span>เปิดในแท็บใหม่</span>
        </a>
        <button ref="closeBtn" class="mv-btn" title="ปิด (Esc)" @click="emit('close')"><Icon name="x" /><span>ปิด</span></button>
      </div>
      <button v-if="many" class="mv-nav prev" aria-label="รูปก่อนหน้า" @click="go(-1)"><Icon name="chevronLeft" /></button>
      <div class="mv-stage" :class="{ zoom }" @click.self="emit('close')">
        <video v-if="cur.type === 'video'" :key="cur.url" :src="cur.url" controls autoplay playsinline></video>
        <img
          v-else
          :key="cur.url"
          :src="cur.url"
          :alt="cur.name ?? 'รูปภาพ'"
          :title="zoom ? 'กดเพื่อย่อให้พอดีจอ' : 'กดเพื่อดูขนาดจริง'"
          @click="zoom = !zoom"
        />
      </div>
      <button v-if="many" class="mv-nav next" aria-label="รูปถัดไป" @click="go(1)"><Icon name="chevronRight" /></button>
    </div>
  </Teleport>
</template>

<style scoped>
.mv {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(12, 18, 15, 0.88);
}
.mv-top {
  position: absolute;
  top: 12px;
  right: 16px;
  left: 16px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  z-index: 2;
}
.mv-n {
  margin-right: auto;
  color: #dfe7e3;
  font-size: 13px;
  line-height: 20px;
}
.mv-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 13px;
  line-height: 20px;
  text-decoration: none;
}
.mv-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}
.mv-btn :deep(.ic) {
  width: 16px;
  height: 16px;
}
.mv-stage {
  width: 100%;
  height: 100%;
  padding: 60px 72px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
}
.mv-stage img,
.mv-stage video {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 6px;
}
.mv-stage img {
  cursor: zoom-in;
}
.mv-stage.zoom {
  align-items: flex-start;
  justify-content: flex-start;
}
.mv-stage.zoom img {
  max-width: none;
  max-height: none;
  margin: auto;
  cursor: zoom-out;
}
.mv-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}
.mv-nav:hover {
  background: rgba(255, 255, 255, 0.24);
}
.mv-nav.prev {
  left: 16px;
}
.mv-nav.next {
  right: 16px;
}
.mv-nav :deep(.ic) {
  width: 22px;
  height: 22px;
}
</style>
